import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import { ApiError } from '@/api/processStepsApi'

/** A provider as listed on the setup page (`GET /sso/setup`, users.manage). */
export interface SsoSetupProvider {
  name: string
  displayName: string
  emailDomains: string[]
  enabled: boolean
  signOnUrl: string
}

export interface SsoSetup {
  configured: boolean
  entityId: string
  acsUrl: string
  nameIdFormat: string
  providers: SsoSetupProvider[]
}

/** A stored provider (`GET /sso/providers`, super-admin). */
export interface SsoProvider {
  id: string
  name: string
  displayName: string
  metadataUrl: string | null
  metadataXml: string | null
  emailDomains: string[]
  enabled: boolean
}

export interface SsoProviderInput {
  name?: string
  displayName?: string
  metadataUrl?: string
  metadataXml?: string
  emailDomains?: string[]
}

/** Result of linking users to a provider's domains. */
export interface SsoLinkSummary {
  linked: number
  failed: { email: string; error?: string }[]
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const res = await authFetch(`${BASE}${path}`, { ...init, headers: authHeaders(init?.headers) })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let body: unknown = text
    try {
      body = JSON.parse(text)
    } catch {
      // not JSON; keep the raw text
    }
    throw new ApiError(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`, res.status, body)
  }
  return res
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(path, init)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** `attachment; filename="x.xml"` → `x.xml`. */
export function filenameFrom(disposition: string | null, fallback: string): string {
  return disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)?.[1] ?? fallback
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })
const provider = (name: string) => `/sso/providers/${encodeURIComponent(name)}`

export const ssoSetupApi = {
  setup: () => request<SsoSetup>('/sso/setup'),

  /** Fetched with the session, not a plain link: the routes need the bearer token. */
  download: async (file: 'metadata.xml' | 'certificate.pem' | 'certificate.cer') => {
    const res = await send(`/sso/setup/${file}`)
    return {
      blob: await res.blob(),
      // Cross-origin, Content-Disposition is readable only if the API exposes it.
      filename: filenameFrom(res.headers.get('Content-Disposition'), file),
    }
  },

  listProviders: () => request<SsoProvider[]>('/sso/providers'),
  createProvider: (input: SsoProviderInput) =>
    request<SsoLinkSummary & { provider: SsoProvider }>('/sso/providers', json('POST', input)),
  updateProvider: (name: string, input: SsoProviderInput) =>
    request<Partial<SsoLinkSummary> & { provider?: SsoProvider }>(provider(name), json('PATCH', input)),
  enableProvider: (name: string) => request<SsoProvider>(`${provider(name)}/enable`, { method: 'POST' }),
  disableProvider: (name: string) => request<SsoProvider>(`${provider(name)}/disable`, { method: 'POST' }),
  deleteProvider: (name: string) => request<void>(provider(name), { method: 'DELETE' }),
}
